import {
  doc,
  setDoc,
  getDoc,
  collection,
  addDoc,
  onSnapshot,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  collectionGroup,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { firestore, storage, functions } from '../config/firebase';
import { exportProperty, saveProperty } from './dataService';
import { copyGeneralDossierToProperty } from './generalDossierService';
import { isOwnedDeal } from '../utils/dealPurpose';

const SHARES_NDA_STORAGE_PREFIX = 'shares-nda';

const COLLECTION = 'shares';
const SCENARIOS_SUB = 'scenarios';
const MESSAGES_SUB = 'messages';
const PARTICIPANTS_SUB = 'participants';
const USERS_COLLECTION = 'users';
const SHARED_WITH_ME = 'sharedWithMe';
const PROPERTIES = 'properties';
const SHARE_REVOKED_SUB = 'shareRevokedRecipients';

const revokeDealRecipientSharedWithMeCallable = httpsCallable(functions, 'revokeDealRecipientSharedWithMe');
const restoreDealRecipientSharedWithMeCallable = httpsCallable(functions, 'restoreDealRecipientSharedWithMe');

async function deleteShareSubcollectionDocs(shareToken, subName) {
  if (!shareToken || !subName) return;
  const colRef = collection(firestore, COLLECTION, shareToken, subName);
  const snap = await getDocs(colRef);
  await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
}

function generateToken() {
  return crypto.randomUUID().replace(/-/g, '');
}

/**
 * Create a share link for a property (and optionally a specific scenario).
 * @param {string} uid - Owner uid (cédant)
 * @param {object} opts - { propertyId, scenarioId?, access, allowSubScenariosEdit?, openToCession?, anonymizeAddress?, ndaFileRef?, sharedByBroker?, brokerMandatedSale?, purchaseOffersModuleActive?, liveSync?, ownedDiligence? }
 * openToCession: destinataires peuvent demander la data room / cession (NDA, etc.)
 * brokerMandatedSale: mandat courtier vente (offres), incompatible avec openToCession.
 * purchaseOffersModuleActive: si true avec brokerMandatedSale, pose purchaseOffersWorkflowCommitted (immuable côté métier).
 * liveSync / ownedDiligence: immeuble détenu — snapshot rafraîchi à la sauvegarde ; diligence sans cession.
 * @returns {Promise<{ token: string, url: string, snapshot }>}
 */
export async function createShare(uid, opts) {
  const {
    propertyId,
    scenarioId = null,
    access = 'read',
    allowSubScenariosEdit = false,
    openToCession = false,
    anonymizeAddress = false,
    ndaFileRef = null,
    pocketDealMode = false,
    pocketRecipientClientId = null,
    pocketCampaignId = null,
    sharedByBroker = false,
    brokerMandatedSale = false,
    purchaseOffersModuleActive = false,
    liveSync = false,
    ownedDiligence = false,
  } = opts;
  const snapshot = await exportProperty(propertyId);
  if (!snapshot) throw new Error('Property not found');

  const owned = isOwnedDeal(snapshot.property);
  const forcedAccess = owned ? 'read' : access;
  const forcedAllowSub = owned ? false : !!allowSubScenariosEdit;
  const forcedOpenToCession = owned ? false : !!openToCession;
  const mandated = owned ? false : !!brokerMandatedSale;
  const forcedLiveSync = owned || !!liveSync;
  const forcedOwnedDiligence =
    owned &&
    (!!ownedDiligence || !!anonymizeAddress || !!(ndaFileRef && ndaFileRef.storagePath));

  if (mandated && forcedOpenToCession) {
    throw new Error('brokerMandatedSale and openToCession cannot both be true');
  }
  if (mandated && !purchaseOffersModuleActive) {
    throw new Error('Purchase offers module must be enabled to create a mandated sale share');
  }
  const token = generateToken();
  const shareRef = doc(firestore, COLLECTION, token);
  const purchaseOffersWorkflowCommitted = mandated && !!purchaseOffersModuleActive;
  await setDoc(shareRef, {
    uid,
    propertyId,
    scenarioId,
    access: forcedAccess,
    allowSubScenariosEdit: forcedAllowSub,
    openToCession: forcedOpenToCession,
    brokerMandatedSale: mandated,
    purchaseOffersWorkflowCommitted,
    anonymizeAddress: !!anonymizeAddress,
    ndaFileRef: ndaFileRef && ndaFileRef.storagePath ? ndaFileRef : null,
    pocketDealMode: !!pocketDealMode,
    pocketRecipientClientId: pocketRecipientClientId || null,
    pocketCampaignId: pocketCampaignId || null,
    sharedByBroker: !!sharedByBroker,
    liveSync: forcedLiveSync,
    ownedDiligence: forcedOwnedDiligence,
    dealPurpose: owned ? 'owned' : (snapshot.property?.dealPurpose || 'acquisition'),
    recipientCount: 0,
    createdAt: new Date(),
    snapshot: {
      property: snapshot.property,
      scenarios: snapshot.scenarios || [],
    },
  });
  if (typeof window === 'undefined') return { token, url: '', snapshot };
  const hash = window.location.hash || '#/';
  const sep = hash.includes('?') ? '&' : '?';
  const url = window.location.origin + window.location.pathname + hash + sep + 'share=' + token;
  return { token, url, snapshot };
}

/**
 * Refresh snapshot on all liveSync shares for a property (owner-only query).
 * Keeps token-share recipients up to date without granting them property read.
 */
export async function refreshLiveShareSnapshots(propertyId, ownerUid) {
  if (!propertyId || !ownerUid) return;
  const shares = await getSharesByProperty(ownerUid, propertyId);
  const live = shares.filter((s) => s.liveSync === true);
  if (live.length === 0) return;
  const snapshot = await exportProperty(propertyId);
  if (!snapshot) return;
  const payload = {
    snapshot: {
      property: snapshot.property,
      scenarios: snapshot.scenarios || [],
    },
    snapshotUpdatedAt: new Date(),
  };
  await Promise.all(
    live.map((s) => updateDoc(doc(firestore, COLLECTION, s.id), payload)),
  );
}

/**
 * Update share document (e.g. add ndaFileRef after upload).
 * @param {string} token - Share token
 * @param {object} data - Fields to merge (e.g. { ndaFileRef })
 */
export async function updateShare(token, data) {
  if (!token) return;
  const shareRef = doc(firestore, COLLECTION, token);
  await updateDoc(shareRef, data);
}
/**
 * Upload NDA file for a share. Call after createShare. Then call updateShare(token, { ndaFileRef }) with the returned ref.
 * @param {string} token - Share token
 * @param {File} file - NDA file
 * @returns {Promise<{ storagePath: string, name: string, size: number, uploadedAt: string }>}
 */
export async function uploadShareNda(token, file) {
  if (!token || !file) throw new Error('token and file required');
  const fileId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  const safeName = (file.name || 'nda.pdf').replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `${SHARES_NDA_STORAGE_PREFIX}/${token}/${fileId}_${safeName}`;
  const storageRefPath = ref(storage, path);
  await uploadBytes(storageRefPath, file);
  const uploadedAt = new Date().toISOString();
  return { storagePath: path, name: file.name || safeName, size: file.size, uploadedAt };
}

/**
 * Get download URL for a share's NDA file.
 * @param {string} storagePath - From ndaFileRef.storagePath
 * @returns {Promise<string>}
 */
export async function getShareNdaDownloadUrl(storagePath) {
  if (!storagePath) throw new Error('storagePath required');
  const r = ref(storage, storagePath);
  return getDownloadURL(r);
}

/** Read `share` query param from the hash (e.g. `/#/?share=TOKEN`). */
export function getShareTokenFromLocationHash() {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash || '';
  const qs = hash.indexOf('?') >= 0 ? hash.slice(hash.indexOf('?')) : '';
  const v = new URLSearchParams(qs).get('share');
  return v && typeof v === 'string' ? v : null;
}

/** Pocket deal recipient client id from hash query (?pc= or &pc=). */
export function getPocketClientIdFromLocationHash() {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash || '';
  const qs = hash.indexOf('?') >= 0 ? hash.slice(hash.indexOf('?')) : '';
  const v = new URLSearchParams(qs).get('pc');
  return v && typeof v === 'string' ? v.trim() : null;
}

/**
 * Get share data by token (allowed for anyone with the link).
 */
export async function getShare(token) {
  if (!token) return null;
  const ref = doc(firestore, COLLECTION, token);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * Live listener on a share document (e.g. liveSync snapshot refresh).
 * @returns {() => void} unsubscribe
 */
export function subscribeShare(token, callback) {
  if (!token) {
    callback(null);
    return () => {};
  }
  const ref = doc(firestore, COLLECTION, token);
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        callback(null);
        return;
      }
      callback({ id: snap.id, ...snap.data() });
    },
    (err) => {
      console.warn('subscribeShare', err?.code, err?.message);
      callback(null);
    },
  );
}

/**
 * Build a small preview from snapshot.property for sharedWithMe card.
 * @param {object} snapshot - share snapshot
 * @param {boolean} [anonymizeAddress] - if true, mask address and postalCode (e.g. **** *************, Montréal (QC), *** ***)
 */
function buildPreview(snapshot, anonymizeAddress) {
  const p = snapshot?.property || {};
  const masked = !!anonymizeAddress;
  return {
    address: masked ? '**** *************' : (p.address || ''),
    city: p.city || '',
    province: p.province || '',
    postalCode: masked ? '*** ***' : (p.postalCode || ''),
    purchasePrice: p.purchasePrice,
    numberOfUnits: p.numberOfUnits,
    effectiveNetIncome: p.effectiveNetIncome,
    anonymizeAddress: masked,
  };
}

/**
 * Deliver a share to an existing user (appears in their dashboard with "New").
 * recipientCount on the share doc is updated by Cloud Functions (onCreate/onDelete sharedWithMe).
 * @param {string} recipientUid
 * @param {string} fromUid
 * @param {string} shareToken
 * @param {object} snapshot - from createShare result (snapshot.property, snapshot.scenarios)
 * @param {{ anonymizeAddress?: boolean }} [opts] - pass anonymizeAddress so preview is masked for sharedWithMe card
 */
export async function addSharedWithMe(recipientUid, fromUid, shareToken, snapshot, opts = {}) {
  const preview = buildPreview(snapshot, opts.anonymizeAddress);
  const refCol = collection(firestore, USERS_COLLECTION, recipientUid, SHARED_WITH_ME);
  await addDoc(refCol, {
    fromUid,
    shareToken,
    seen: false,
    createdAt: new Date(),
    ...preview,
  });
}

/**
 * One-time fetch of shared-with-me items (for refresh/filter when investor views the list).
 */
export async function getSharedWithMeOnce(uid) {
  if (!uid) return [];
  const ref = collection(firestore, USERS_COLLECTION, uid, SHARED_WITH_ME);
  const snapshot = await getDocs(ref);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Fetch sharedWithMe once, remove orphans (share no longer exists), update Firestore, return filtered list.
 * Call this when the investor views "Partagés avec moi" so deleted shares disappear without waiting for a snapshot.
 */
export async function refreshSharedWithMeList(uid) {
  if (!uid) return [];
  const items = await getSharedWithMeOnce(uid);
  return filterOrphanSharedWithMe(uid, items);
}

/**
 * Listen to shared-with-me items for the current user.
 * Entries whose share no longer exists (e.g. broker deleted the property) are filtered out
 * and removed from Firestore so they disappear from "Partagés avec moi".
 */
export function getSharedWithMe(uid, callback) {
  if (!uid) return () => {};
  const ref = collection(firestore, USERS_COLLECTION, uid, SHARED_WITH_ME);
  return onSnapshot(
    ref,
    (snapshot) => {
      const items = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      filterOrphanSharedWithMe(uid, items).then(callback).catch((e) => {
        console.warn('getSharedWithMe filterOrphan error', e);
        callback(items);
      });
    },
    (err) => {
      console.warn('getSharedWithMe error:', err?.code, err?.message);
      callback([]);
    }
  );
}

/**
 * Remove entries whose share no longer exists (broker deleted the property) and return the rest.
 * The current user (recipient) can delete their own sharedWithMe docs, so we clean orphans here.
 */
async function filterOrphanSharedWithMe(uid, items) {
  if (!items.length) return items;
  const results = await Promise.all(
    items.map(async (item) => {
      const share = await getShare(item.shareToken);
      return { item, shareExists: !!share };
    })
  );
  const toKeep = results.filter((r) => r.shareExists).map((r) => r.item);
  const toRemove = results.filter((r) => !r.shareExists).map((r) => r.item);
  await Promise.all(
    toRemove.map((item) =>
      removeSharedWithMe(uid, item.id, item.shareToken).catch((e) =>
        console.warn('removeSharedWithMe orphan', item.id, e)
      )
    )
  );
  return toKeep;
}

/**
 * Mark a shared-with-me item as seen.
 */
export async function markSharedWithMeSeen(recipientUid, docId) {
  const ref = doc(firestore, USERS_COLLECTION, recipientUid, SHARED_WITH_ME, docId);
  await updateDoc(ref, { seen: true });
}

/**
 * Delete all scenarios in a share that were created by the given user (e.g. when they remove the share from their dashboard).
 */
export async function deleteShareScenariosByCreator(token, creatorUid) {
  if (!token || !creatorUid) return;
  const snapshot = await getDocs(shareScenariosRef(token));
  const toDelete = snapshot.docs.filter((d) => d.data().createdByUid === creatorUid);
  await Promise.all(toDelete.map((d) => deleteDoc(d.ref)));
}

/**
 * Remove a shared-with-me item from the recipient's dashboard (does not affect the creator's property).
 * If shareToken is provided, also deletes all sub-scenarios created by this recipient in that share.
 * If the share was already deleted (e.g. broker deleted the property), we still remove the sharedWithMe doc.
 */
export async function removeSharedWithMe(recipientUid, docId, shareToken = null) {
  if (shareToken && recipientUid) {
    try {
      await deleteShareScenariosByCreator(shareToken, recipientUid);
    } catch (e) {
      console.warn('removeSharedWithMe: deleteShareScenariosByCreator skipped (share may be deleted)', e);
    }
  }
  const ref = doc(firestore, USERS_COLLECTION, recipientUid, SHARED_WITH_ME, docId);
  await deleteDoc(ref);
}

// --- Share scenarios (when access === 'write') ---

function shareScenariosRef(token) {
  return collection(firestore, COLLECTION, token, SCENARIOS_SUB);
}

/**
 * Listen to scenarios added to a share (subcollection). Used when share access is 'write'.
 */
export function getShareScenarios(token, callback, parentScenarioId = null) {
  if (!token) return () => {};
  const ref = shareScenariosRef(token);
  const q = parentScenarioId != null
    ? query(ref, where('parentScenarioId', '==', parentScenarioId))
    : ref;
  return onSnapshot(
    q,
    (snapshot) => {
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(list);
    },
    (err) => {
      console.warn('getShareScenarios error:', err?.code, err?.message);
      callback([]);
    }
  );
}

/**
 * @param {string} token
 * @param {object} scenario
 * @param {{ createdByUid: string, createdByName?: string }} [creatorInfo] - creator of the scenario (for visibility: only that client sees it; broker sees all with this label)
 */
export async function saveShareScenario(token, scenario, creatorInfo = null) {
  const ref = shareScenariosRef(token);
  const { id: _id, createdByUid: _cu, createdByName: _cn, ...data } = scenario;
  const payload = {
    ...data,
    type: data.type || 'initialFinancing',
    ...(creatorInfo && {
      createdByUid: creatorInfo.createdByUid,
      createdByName: creatorInfo.createdByName || '',
    }),
  };
  const docRef = await addDoc(ref, payload);
  return docRef.id;
}

export async function updateShareScenario(token, scenarioId, data) {
  const ref = doc(firestore, COLLECTION, token, SCENARIOS_SUB, scenarioId);
  const { id: _id, createdByUid: _cu, createdByName: _cn, ...rest } = data;
  await updateDoc(ref, rest);
}

export async function deleteShareScenario(token, scenarioId) {
  const ref = doc(firestore, COLLECTION, token, SCENARIOS_SUB, scenarioId);
  await deleteDoc(ref);
}

export async function duplicateShareScenario(token, scenario, creatorInfo = null) {
  const { id: _id, createdByUid: _cu, createdByName: _cn, ...data } = scenario;
  return saveShareScenario(token, data, creatorInfo);
}

/**
 * One-time read of all scenarios in a share (e.g. for broker aggregating from multiple shares).
 */
export async function getShareScenariosOnce(token) {
  if (!token) return [];
  const snapshot = await getDocs(shareScenariosRef(token));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * List shares for a given property (by owner uid). Used by broker to load all client sub-scenarios.
 * Requires composite index on (uid, propertyId) if not already present.
 */
export async function getSharesByProperty(uid, propertyId) {
  if (!uid || !propertyId) return [];
  const ref = collection(firestore, COLLECTION);
  const q = query(ref, where('uid', '==', uid), where('propertyId', '==', propertyId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Mise à jour en direct (ex. recipientCount quand un destinataire retire le partage de son tableau de bord).
 * @param {(shares: Array<{ id: string, [key: string]: any }>) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeSharesByProperty(uid, propertyId, callback) {
  if (!uid || !propertyId) {
    callback([]);
    return () => {};
  }
  const ref = collection(firestore, COLLECTION);
  const q = query(ref, where('uid', '==', uid), where('propertyId', '==', propertyId));
  return onSnapshot(
    q,
    (snapshot) => {
      callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    },
    (err) => {
      console.warn('subscribeSharesByProperty', err?.code, err?.message);
      callback([]);
    },
  );
}

/**
 * List all share docs owned by this user (for account deletion).
 * @param {string} uid
 * @returns {Promise<Array<{ id: string, [key: string]: any }>>}
 */
export async function getSharesByUser(uid) {
  if (!uid) return [];
  const ref = collection(firestore, COLLECTION);
  const q = query(ref, where('uid', '==', uid));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Delete all shares owned by this user (sharedWithMe entries, share scenarios, share docs).
 * Call when deleting user account (for shares not tied to a property, or after properties are cleaned).
 */
export async function deleteAllSharesForUser(uid) {
  if (!uid) return;
  const shares = await getSharesByUser(uid);
  for (const share of shares) {
    try {
      await deleteSharedWithMeEntriesByShareToken(share.id);
    } catch (e) {
      console.warn('deleteAllSharesForUser: could not delete sharedWithMe for share', share.id, e);
    }
    try {
      await deleteShareSubcollectionDocs(share.id, MESSAGES_SUB);
    } catch (e) {
      console.warn('deleteAllSharesForUser: delete messages', share.id, e);
    }
    try {
      await deleteShareSubcollectionDocs(share.id, PARTICIPANTS_SUB);
    } catch (e) {
      console.warn('deleteAllSharesForUser: delete participants', share.id, e);
    }
    try {
      const scenariosSnap = await getDocs(shareScenariosRef(share.id));
      await Promise.all(scenariosSnap.docs.map((d) => deleteDoc(d.ref)));
    } catch (e) {
      console.warn('deleteAllSharesForUser: delete share scenarios', share.id, e);
    }
    try {
      await deleteDoc(doc(firestore, COLLECTION, share.id));
    } catch (e) {
      throw new Error('delete share doc(' + share.id + '): ' + (e?.message || e));
    }
  }
}

/**
 * Total number of people who have this property in their dashboard (across all share links for it).
 */
export async function getPropertyShareRecipientCount(uid, propertyId) {
  const shares = await getSharesByProperty(uid, propertyId);
  if (!shares.length) return 0;
  const counts = await Promise.all(shares.map((s) => getShareRecipientCount(s.id)));
  return counts.reduce((a, b) => a + b, 0);
}

/**
 * Number of users who have this share in their "Partagés avec moi" (dashboard).
 * Reads recipientCount from the share document (updated by Cloud Functions on create/delete of sharedWithMe).
 */
export async function getShareRecipientCount(shareToken) {
  if (!shareToken) return 0;
  const shareRef = doc(firestore, COLLECTION, shareToken);
  const snap = await getDoc(shareRef);
  if (!snap.exists()) return 0;
  const data = snap.data();
  const n = data.recipientCount;
  return typeof n === 'number' ? Math.max(0, n) : 0;
}

/**
 * Delete all sharedWithMe entries that reference this share token (collection group query).
 * Called by the share owner when they delete the property so clients lose the shared entry.
 */
export async function deleteSharedWithMeEntriesByShareToken(shareToken) {
  if (!shareToken) return;
  const ref = collectionGroup(firestore, SHARED_WITH_ME);
  const q = query(ref, where('shareToken', '==', shareToken));
  const snapshot = await getDocs(q);
  await Promise.all(snapshot.docs.map((d) => deleteDoc(d.ref)));
}

/**
 * Get all sharedWithMe entries for a share token (path: users/{userId}/sharedWithMe/{docId}).
 * @returns {Promise<Array<{ userId: string, docId: string }>>}
 */
export async function getSharedWithMeEntriesByShareToken(shareToken) {
  if (!shareToken) return [];
  const ref = collectionGroup(firestore, SHARED_WITH_ME);
  const q = query(ref, where('shareToken', '==', shareToken));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => {
    const pathParts = d.ref.path.split('/');
    const userId = pathParts[1];
    return { userId, docId: d.id };
  });
}

/**
 * Remove the sharedWithMe entry for a specific recipient (e.g. cessionnaire after finalize).
 */
export async function removeSharedWithMeForRecipient(shareToken, recipientUid) {
  if (!shareToken || !recipientUid) return;
  const entries = await getSharedWithMeEntriesByShareToken(shareToken);
  const entry = entries.find((e) => e.userId === recipientUid);
  if (entry) await removeSharedWithMe(recipientUid, entry.docId, shareToken);
}

/**
 * Finalize cession: create property for cessionnaire, update share, remove cessionnaire from sharedWithMe, revoke document access.
 * @param {string} shareToken
 * @param {string} interestId - the interest to choose as cessionnaire (must have status cession_submitted)
 * @returns {Promise<string>} new property id for cessionnaire
 */
export async function finalizeCession(shareToken, interestId) {
  if (!shareToken || !interestId) throw new Error('shareToken and interestId required');
  const shareRef = doc(firestore, COLLECTION, shareToken);
  const interestRef = doc(firestore, COLLECTION, shareToken, 'interests', interestId);
  const [shareSnap, interestSnap] = await Promise.all([getDoc(shareRef), getDoc(interestRef)]);
  if (!shareSnap.exists() || !interestSnap.exists()) throw new Error('Share or interest not found');
  const shareData = shareSnap.data();
  const interestData = interestSnap.data();
  if (interestData.status !== 'cession_submitted') throw new Error('Interest must have cession_submitted status');
  const cessionnaireUid = interestData.recipientUid;
  const snapshot = shareData.snapshot || {};
  const propData = snapshot.property || {};
  const { id: _id, ...propertyFields } = propData;
  const newPropertyId = await saveProperty({ ...propertyFields, uid: cessionnaireUid });
  if (!newPropertyId) throw new Error('Failed to create property');

  const scenarios = await getShareScenariosOnce(shareToken);
  const scenariosCol = collection(firestore, 'properties', newPropertyId, 'scenarios');
  for (const sc of scenarios) {
    const { id: _sid, ...scData } = sc;
    await addDoc(scenariosCol, scData);
  }

  await copyGeneralDossierToProperty(shareData.propertyId, newPropertyId, cessionnaireUid);

  await updateDoc(shareRef, {
    cessionCompletedAt: new Date(),
    cessionnaireUid,
    cessionnairePropertyId: newPropertyId,
  });

  await removeSharedWithMeForRecipient(shareToken, cessionnaireUid);

  const remainingEntries = await getSharedWithMeEntriesByShareToken(shareToken);
  await Promise.all(
    remainingEntries.map((entry) => {
      const refDoc = doc(firestore, USERS_COLLECTION, entry.userId, SHARED_WITH_ME, entry.docId);
      return updateDoc(refDoc, { cessionRevoked: true });
    })
  );

  const documentAccessCol = collection(firestore, 'properties', shareData.propertyId, 'documentAccess');
  const accessSnap = await getDocs(documentAccessCol);
  await Promise.all(accessSnap.docs.map((d) => deleteDoc(d.ref)));

  return newPropertyId;
}

/**
 * When the broker deletes a property: remove the share from all clients' dashboards and delete share docs.
 * If deleting sharedWithMe entries fails (e.g. permissions), we still delete the share doc and scenarios
 * so the broker can delete the property; investors will see a missing share when they open the link.
 */
/**
 * UIDs with a participants doc for this share (typically users who added the deal to their dashboard).
 * @param {string} shareToken
 * @returns {Promise<string[]>}
 */
export async function listShareParticipantUids(shareToken) {
  if (!shareToken) return [];
  const colRef = collection(firestore, COLLECTION, shareToken, PARTICIPANTS_SUB);
  const snap = await getDocs(colRef);
  return snap.docs.map((d) => d.id);
}

/**
 * Participant rows for share owners (requires Firestore rules: owner may read all participant docs).
 * @param {string} shareToken
 * @returns {Promise<Array<{ uid: string, displayName: string, role: string }>>}
 */
export async function listShareParticipantsForOwner(shareToken) {
  if (!shareToken) return [];
  const colRef = collection(firestore, COLLECTION, shareToken, PARTICIPANTS_SUB);
  const snap = await getDocs(colRef);
  return snap.docs.map((d) => {
    const data = d.data() || {};
    const dn = typeof data.displayName === 'string' ? data.displayName.trim() : '';
    return {
      uid: d.id,
      displayName: dn,
      role: typeof data.role === 'string' ? data.role : '',
    };
  });
}

/**
 * @param {string} propertyId
 * @param {(rows: Array<{ id: string } & Record<string, unknown>>) => void} onList
 * @param {(err: Error) => void} [onError]
 */
export function subscribeShareRevokedRecipients(propertyId, onList, onError) {
  if (!propertyId) return () => {};
  const ref = collection(firestore, PROPERTIES, propertyId, SHARE_REVOKED_SUB);
  return onSnapshot(
    ref,
    (snap) => onList(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.warn('subscribeShareRevokedRecipients', err?.code, err?.message);
      onError?.(err);
    },
  );
}

/**
 * Remove recipient from sharedWithMe (CF removes share participants).
 * Records revocation so the owner can reinstate and list revoked users in the UI.
 * @param {{ propertyId: string, recipientUid: string, displayName: string, shareTokens: string[] }} opts
 */
export async function revokeDealShareRecipientAccess(opts) {
  const { propertyId, recipientUid, displayName, shareTokens } = opts;
  if (!propertyId || !recipientUid || !Array.isArray(shareTokens) || shareTokens.length === 0) {
    throw new Error('revokeDealShareRecipientAccess: propertyId, recipientUid and shareTokens required');
  }
  const uniqueTokens = [...new Set(shareTokens.filter((t) => typeof t === 'string' && t.trim()))];
  if (uniqueTokens.length === 0) {
    throw new Error('revokeDealShareRecipientAccess: no valid share tokens');
  }
  try {
    const { data } = await revokeDealRecipientSharedWithMeCallable({
      propertyId,
      recipientUid,
      displayName: (displayName || '').trim().slice(0, 200),
      shareTokens: uniqueTokens,
    });
    if (!data?.ok) throw new Error('server returned not ok');
  } catch (e) {
    const msg = e?.message || e?.code || String(e);
    throw new Error(`revokeDealShareRecipientAccess: ${msg}`);
  }
}

/**
 * Re-add sharedWithMe for each stored share token and delete the revocation record.
 * @param {{ propertyId: string, ownerUid: string, recipientUid: string }} opts
 */
export async function restoreDealShareRecipientAccess(opts) {
  const { propertyId, ownerUid, recipientUid } = opts;
  if (!propertyId || !ownerUid || !recipientUid) {
    throw new Error('restoreDealShareRecipientAccess: propertyId, ownerUid, recipientUid required');
  }
  try {
    const { data } = await restoreDealRecipientSharedWithMeCallable({ propertyId, recipientUid });
    if (!data?.ok) throw new Error('server returned not ok');
  } catch (e) {
    const msg = e?.message || e?.code || String(e);
    throw new Error(`restoreDealShareRecipientAccess: ${msg}`);
  }
}

export async function cleanupPropertyShares(ownerUid, propertyId) {
  if (!ownerUid || !propertyId) return;
  let shares;
  try {
    shares = await getSharesByProperty(ownerUid, propertyId);
  } catch (e) {
    throw new Error('getSharesByProperty: ' + (e?.message || e));
  }
  for (const share of shares) {
    try {
      await deleteSharedWithMeEntriesByShareToken(share.id);
    } catch (e) {
      console.warn('cleanupPropertyShares: could not delete sharedWithMe entries for share', share.id, e);
      // Continue: we still delete the share doc so the property can be removed; investors will get "share missing" when opening.
    }
    try {
      await deleteShareSubcollectionDocs(share.id, MESSAGES_SUB);
    } catch (e) {
      console.warn('cleanupPropertyShares: delete messages', share.id, e);
    }
    try {
      await deleteShareSubcollectionDocs(share.id, PARTICIPANTS_SUB);
    } catch (e) {
      console.warn('cleanupPropertyShares: delete participants', share.id, e);
    }
    try {
      const scenariosSnap = await getDocs(shareScenariosRef(share.id));
      await Promise.all(scenariosSnap.docs.map((d) => deleteDoc(d.ref)));
    } catch (e) {
      throw new Error('delete share scenarios(' + share.id + '): ' + (e?.message || e));
    }
    try {
      await deleteDoc(doc(firestore, COLLECTION, share.id));
    } catch (e) {
      throw new Error('delete share doc(' + share.id + '): ' + (e?.message || e));
    }
  }
}
